// src/components/common/SensitiveDataGuideModal.js
// Guide pratique pour la manipulation des données sensibles — pour les utilisateurs
import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, ShieldCheck } from 'lucide-react';

const SensitiveDataGuideModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const recommendations = [
        {
            title: t('sensitiveData.classification.title'),
            items: [
                t('sensitiveData.classification.item1'),
                t('sensitiveData.classification.item2'),
                t('sensitiveData.classification.item3')
            ]
        },
        {
            title: t('sensitiveData.exports.title'),
            items: [
                t('sensitiveData.exports.item1'),
                t('sensitiveData.exports.item2'),
                t('sensitiveData.exports.item3')
            ]
        },
        {
            title: t('sensitiveData.access.title'),
            items: [
                t('sensitiveData.access.item1'),
                t('sensitiveData.access.item2'),
                t('sensitiveData.access.item3')
            ]
        },
        {
            title: t('sensitiveData.workstation.title'),
            items: [
                t('sensitiveData.workstation.item1'),
                t('sensitiveData.workstation.item2'),
                t('sensitiveData.workstation.item3')
            ]
        },
        {
            title: t('sensitiveData.session.title'),
            items: [
                t('sensitiveData.session.item1'),
                t('sensitiveData.session.item2'),
                t('sensitiveData.session.item3')
            ]
        }
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-emerald-300" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">
                                {t('sensitiveData.title')}
                            </h2>
                            <p className="text-xs text-emerald-200">
                                {t('sensitiveData.subtitle')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content — scrollable */}
                <div className="overflow-y-auto flex-1 p-6 space-y-4">
                    {/* Intro */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <p className="text-sm text-emerald-800 leading-relaxed">
                            {t('sensitiveData.intro')}
                        </p>
                    </div>

                    {recommendations.map((section, idx) => (
                        <div
                            key={idx}
                            className="bg-gray-50 rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                        >
                            <h3 className="font-semibold text-gray-800 text-sm mb-3">
                                {idx + 1}. {section.title}
                            </h3>
                            <ul className="space-y-2">
                                {section.items.map((item, i) => (
                                    <li key={i} className="text-gray-600 text-sm leading-relaxed flex items-start gap-2">
                                        <span className="text-emerald-500 mt-1 flex-shrink-0">•</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 flex justify-end flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-sm"
                    >
                        {t('sensitiveData.understood')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SensitiveDataGuideModal;
